// src/utils/tableModel.js

// 🪑 Table Availability Status
export const TABLE_AVAILABILITY = {
  AVAILABLE: 'available',
  RESERVED: 'reserved'
}

// 🎨 Table Availability Configuration
export const AVAILABILITY_CONFIG = {
  [TABLE_AVAILABILITY.AVAILABLE]: {
    label: 'Available',
    value: 'available',
    icon: '✅',
    color: 'green',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    badgeClass: 'bg-green-100 text-green-800'
  },
  [TABLE_AVAILABILITY.RESERVED]: {
    label: 'Reserved',
    value: 'reserved',
    icon: '🔒',
    color: 'red',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
    badgeClass: 'bg-red-100 text-red-800'
  }
}

// 📋 Table Schema
export const TableSchema = {
  _id: '',
  number: '',
  branch: '', // Branch ID
  capacity: 0,
  availability: TABLE_AVAILABILITY.AVAILABLE,
  isActive: true,
  createdAt: null,
  updatedAt: null,
  createdBy: null,
  // Populated fields (for display)
  branchDetails: null
}

// ✅ Validation Rules
export const VALIDATION_RULES = {
  number: {
    required: true,
    minLength: 1,
    maxLength: 20,
    pattern: /^[A-Za-z0-9\-_]+$/,
    message: 'Table number must be 1-20 characters and contain only letters, numbers, hyphens, and underscores'
  },
  branch: {
    required: true,
    message: 'Please select a branch'
  },
  capacity: {
    required: true,
    min: 1,
    max: 50,
    message: 'Capacity must be between 1 and 50'
  },
  availability: {
    required: true,
    enum: Object.values(TABLE_AVAILABILITY),
    message: 'Please select a valid availability status'
  }
}

// 🔄 Table Model Utilities
export class TableModel {
  
  // Create a new table instance
  static create(data = {}) {
    return {
      ...TableSchema,
      ...data,
      _id: data._id || `temp_${Date.now()}`,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString()
    }
  }
  
  // Validate table data
  static validate(tableData, isCreation = false) {
    const errors = {}
    
    // Validate table number
    if (!tableData.number || !tableData.number.trim()) {
      errors.number = 'Table number is required'
    } else if (tableData.number.length < VALIDATION_RULES.number.minLength) {
      errors.number = `Table number must be at least ${VALIDATION_RULES.number.minLength} character`
    } else if (tableData.number.length > VALIDATION_RULES.number.maxLength) {
      errors.number = `Table number must not exceed ${VALIDATION_RULES.number.maxLength} characters`
    } else if (!VALIDATION_RULES.number.pattern.test(tableData.number)) {
      errors.number = VALIDATION_RULES.number.message
    }
    
    // Validate branch
    if (!tableData.branch || !tableData.branch.trim()) {
      errors.branch = VALIDATION_RULES.branch.message
    }
    
    // Validate capacity
    if (tableData.capacity === undefined || tableData.capacity === null || tableData.capacity === '') {
      errors.capacity = 'Capacity is required'
    } else if (isNaN(tableData.capacity) || parseInt(tableData.capacity) < VALIDATION_RULES.capacity.min) {
      errors.capacity = `Capacity must be at least ${VALIDATION_RULES.capacity.min}`
    } else if (parseInt(tableData.capacity) > VALIDATION_RULES.capacity.max) {
      errors.capacity = `Capacity must not exceed ${VALIDATION_RULES.capacity.max}`
    }
    
    // Validate availability (only for updates, not creation)
    if (!isCreation && tableData.availability !== undefined) {
      if (!tableData.availability) {
        errors.availability = 'Availability status is required'
      } else if (!Object.values(TABLE_AVAILABILITY).includes(tableData.availability)) {
        errors.availability = VALIDATION_RULES.availability.message
      }
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }
  
  // Validate partial table data (for updates)
  static validatePartial(tableData) {
    const errors = {}
    
    // Only validate fields that are provided
    if (tableData.number !== undefined) {
      if (!tableData.number.trim()) {
        errors.number = 'Table number is required'
      } else if (tableData.number.length < VALIDATION_RULES.number.minLength) {
        errors.number = `Table number must be at least ${VALIDATION_RULES.number.minLength} character`
      } else if (tableData.number.length > VALIDATION_RULES.number.maxLength) {
        errors.number = `Table number must not exceed ${VALIDATION_RULES.number.maxLength} characters`
      } else if (!VALIDATION_RULES.number.pattern.test(tableData.number)) {
        errors.number = VALIDATION_RULES.number.message
      }
    }
    
    if (tableData.branch !== undefined && (!tableData.branch || !tableData.branch.trim())) {
      errors.branch = VALIDATION_RULES.branch.message
    }
    
    if (tableData.capacity !== undefined) {
      if (isNaN(tableData.capacity) || tableData.capacity < VALIDATION_RULES.capacity.min) {
        errors.capacity = `Capacity must be at least ${VALIDATION_RULES.capacity.min}`
      } else if (tableData.capacity > VALIDATION_RULES.capacity.max) {
        errors.capacity = `Capacity must not exceed ${VALIDATION_RULES.capacity.max}`
      }
    }
    
    if (tableData.availability !== undefined && !Object.values(TABLE_AVAILABILITY).includes(tableData.availability)) {
      errors.availability = VALIDATION_RULES.availability.message
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }
  
  // Format table for API
  static formatForAPI(tableData) {
    return {
      number: tableData.number?.trim(),
      branch: tableData.branch,
      capacity: parseInt(tableData.capacity),
      availability: tableData.availability,
      isActive: tableData.isActive !== undefined ? tableData.isActive : true
    }
  }
  
  // Get table display name
  static getDisplayName(table) {
    return `Table ${table.number}`
  }
  
  // Get availability badge class
  static getAvailabilityBadgeClass(availability) {
    return AVAILABILITY_CONFIG[availability]?.badgeClass || 'bg-gray-100 text-gray-800'
  }
  
  // Get availability icon
  static getAvailabilityIcon(availability) {
    return AVAILABILITY_CONFIG[availability]?.icon || '❓'
  }
  
  // Get status badge class
  static getStatusBadgeClass(isActive) {
    return isActive 
      ? 'bg-green-100 text-green-800'
      : 'bg-gray-100 text-gray-800'
  }
  
  // Get table summary statistics
  static getTablesSummary(tables) {
    const summary = {
      total: tables.length,
      active: 0,
      inactive: 0,
      available: 0,
      reserved: 0,
      totalCapacity: 0,
      avgCapacity: 0,
      byBranch: {}
    }
    
    tables.forEach(table => {
      // Status counts
      if (table.isActive) {
        summary.active++
      } else {
        summary.inactive++
      }
      
      // Availability counts
      if (table.availability === TABLE_AVAILABILITY.AVAILABLE) {
        summary.available++
      } else if (table.availability === TABLE_AVAILABILITY.RESERVED) {
        summary.reserved++
      }
      
      // Capacity calculations
      summary.totalCapacity += table.capacity || 0
      
      // Branch breakdown
      const branchName = table.branchDetails?.name || table.branch || 'Unknown'
      if (!summary.byBranch[branchName]) {
        summary.byBranch[branchName] = {
          total: 0,
          available: 0,
          reserved: 0,
          capacity: 0
        }
      }
      summary.byBranch[branchName].total++
      summary.byBranch[branchName].capacity += table.capacity || 0
      if (table.availability === TABLE_AVAILABILITY.AVAILABLE) {
        summary.byBranch[branchName].available++
      } else if (table.availability === TABLE_AVAILABILITY.RESERVED) {
        summary.byBranch[branchName].reserved++
      }
    })
    
    // Calculate average capacity
    summary.avgCapacity = summary.total > 0 ? Math.round(summary.totalCapacity / summary.total) : 0
    
    return summary
  }
  
  // Search tables
  static searchTables(tables, searchTerm) {
    if (!searchTerm || !searchTerm.trim()) return tables
    
    const term = searchTerm.toLowerCase().trim()
    return tables.filter(table => 
      table.number?.toLowerCase().includes(term) ||
      table.branchDetails?.name?.toLowerCase().includes(term) ||
      table.availability?.toLowerCase().includes(term) ||
      table.capacity?.toString().includes(term)
    )
  }
  
  // Filter tables by branch
  static filterByBranch(tables, branchId) {
    if (!branchId || branchId === 'all') return tables
    return tables.filter(table => {
      // Handle different branch field formats
      if (typeof table.branch === 'object' && table.branch._id) {
        // Branch is populated object
        return table.branch._id === branchId
      } else if (typeof table.branch === 'string') {
        // Branch is just ID string
        return table.branch === branchId
      } else if (table.branchDetails && table.branchDetails._id) {
        // Branch details object
        return table.branchDetails._id === branchId
      }
      return false
    })
  }
  
  // Filter tables by availability
  static filterByAvailability(tables, availability) {
    if (!availability || availability === 'all') return tables
    return tables.filter(table => table.availability === availability)
  }
  
  // Filter tables by status
  static filterByStatus(tables, isActive) {
    if (isActive === null || isActive === undefined || isActive === 'all') return tables
    const activeStatus = isActive === 'active' ? true : false
    return tables.filter(table => table.isActive === activeStatus)
  }
  
  // Sort tables
  static sortTables(tables, sortBy = 'number', sortOrder = 'asc') {
    return [...tables].sort((a, b) => {
      let valueA = a[sortBy]
      let valueB = b[sortBy]
      
      // Handle nested properties
      if (sortBy === 'branchName') {
        valueA = a.branchDetails?.name || ''
        valueB = b.branchDetails?.name || ''
      }
      
      // Handle string comparison
      if (typeof valueA === 'string') {
        valueA = valueA.toLowerCase()
        valueB = valueB.toLowerCase()
      }
      
      // Handle date comparison
      if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
        valueA = new Date(valueA).getTime()
        valueB = new Date(valueB).getTime()
      }
      
      if (sortOrder === 'asc') {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0
      }
    })
  }
}

// Export default configuration
export default {
  TABLE_AVAILABILITY,
  AVAILABILITY_CONFIG,
  TableSchema,
  VALIDATION_RULES,
  TableModel
}